/**
 * The single deferred step `BotDriver` ever has in flight — a bot taking its
 * turn, or an unattended claim window resolving — plus the pause switch over
 * it. Split out of `BotDriver` when the pause menu landed, both for the
 * 200-line cap and because "what is queued, and may it run right now?" is its
 * own question with its own edge cases.
 *
 * It holds one step rather than a queue because the driver only ever has one:
 * every transition ends in `advance()`, which cancels whatever was pending and
 * decides afresh.
 *
 * **Pausing keeps the queued step and drops only its `setTimeout`**; resuming
 * re-arms the very same closure with a full fresh delay. The tempting
 * alternative — forget it on pause, re-derive it on resume by calling
 * `advance()` — would re-run the bots' claim collection, drawing from the
 * shared tie-break RNG a second time and quietly changing the hand the player
 * is in the middle of watching.
 */

export class BotTimer {
  private handle: ReturnType<typeof setTimeout> | null = null;
  private queued: { readonly run: () => void; readonly delayMs: number } | null = null;
  private paused = false;

  /** Queue `run`, replacing anything already queued. Fires only while running. */
  schedule(run: () => void, delayMs: number): void {
    this.cancel();
    this.queued = { run, delayMs };
    this.arm();
  }

  /** Drop the queued step for good — unlike pausing, it will not come back. */
  cancel(): void {
    this.disarm();
    this.queued = null;
  }

  isPaused(): boolean {
    return this.paused;
  }

  setPaused(paused: boolean): void {
    if (paused === this.paused) return;
    this.paused = paused;
    if (paused) this.disarm();
    else this.arm();
  }

  private arm(): void {
    const queued = this.queued;
    if (queued === null || this.paused || this.handle !== null) return;
    this.handle = setTimeout(() => {
      this.handle = null;
      this.queued = null;
      queued.run();
    }, queued.delayMs);
  }

  private disarm(): void {
    if (this.handle === null) return;
    clearTimeout(this.handle);
    this.handle = null;
  }
}
